<template><div><component-combinator component-name="DtBadge" /><h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">To flag and draw awareness to a specific element or feature of focus. For example, something is unique about that separates it from other like content.</li>
<li class="d-docsite--list-element">As a notification system with minimal footprint.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">To indicate that interaction by the user is required.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="best-practices" tabindex="-1" class="d-docsite--header-3">Best Practices <a class="header-anchor d-link d-docsite--link d-link" href="#best-practices" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">While the color variant used should not be the sole indicator of information, choose color patterns that users can quickly scan and identify its intention.</li>
<li class="d-docsite--list-element">Avoid long values, favoring a brief scannable word.</li>
</ul>
<h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Since a Badge may often reflect a value within an implied label, ensure a label is announced. For example, via <code v-pre>aria-label</code> or <code v-pre>aria-labeledby</code>.</li>
</ul>
<h2 id="kind" tabindex="-1" class="d-docsite--header-2">Kind <a class="header-anchor d-link d-docsite--link d-link" href="#kind" aria-hidden="true">#</a></h2>
<h3 id="label" tabindex="-1" class="d-docsite--header-3">Label <a class="header-anchor d-link d-docsite--link d-link" href="#label" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-badge text=&quot;Label&quot; /&gt;'>
<dt-badge text="Label" />
</code-example>
<h3 id="count" tabindex="-1" class="d-docsite--header-3">Count <a class="header-anchor d-link d-docsite--link d-link" href="#count" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-badge kind=&quot;count&quot; text=&quot;1&quot; /&gt;'>
<dt-badge kind="count" text="1" />
</code-example>
<h2 id="type" tabindex="-1" class="d-docsite--header-2">Type <a class="header-anchor d-link d-docsite--link d-link" href="#type" aria-hidden="true">#</a></h2>
<table class="d-table dialtone-doc-table d-mbe-200">
  <thead>
    <tr>
      <th>Type</th>
      <th class="d-ws-nowrap">Kind: <span class="d-fw-normal">Label</span></th>
      <th class="d-ws-nowrap">Kind: <span class="d-fw-normal">Count</span></th>
      <th>Usage</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <th class="d-ta-left">Default</th>
      <td>
        <dt-badge text="Label"></dt-badge>
      </td>
      <td>
        <dt-badge kind="count" text="1"></dt-badge>
      </td>
      <td>Default general purpose callout when no implicit semantic meaning applies.</td>
    </tr>
    <tr>
      <th class="d-ta-left">Info</th>
      <td>
        <dt-badge type="info" text="Label"></dt-badge>
      </td>
      <td>
        <dt-badge kind="count" type="info" text="2"></dt-badge>
      </td>
      <td>Used to convey general information that isn't critical or requires action on the user's part.</td>
    </tr>
    <tr>
      <th class="d-ta-left">Positive</th>
      <td>
        <dt-badge type="positive" text="Label"></dt-badge>
      </td>
      <td>
        <dt-badge kind="count" type="positive" text="3"></dt-badge>
      </td>
      <td>Accompanying a successful or otherwise positive action or message</td>
    </tr>
    <tr>
      <th class="d-ta-left">Warning</th>
      <td>
        <dt-badge type="warning" text="Label"></dt-badge>
      </td>
      <td>
        <dt-badge kind="count" type="warning" text="4"></dt-badge>
      </td>
      <td>When a users attention is needed, or action may be required.</td>
    </tr>
    <tr>
      <th class="d-ta-left">Critical</th>
      <td>
        <dt-badge type="critical" text="Label"></dt-badge>
      </td>
      <td>
        <dt-badge kind="count" type="critical" text="5"></dt-badge>
      </td>
      <td>To communicate conditions deemed critical, negative, or dangerous. For example, sensitive state (e.g. recording), must be resolved, or something has failed.</td>
    </tr>
    <tr>
      <th class="d-ta-left">Bulletin</th>
      <td>
        <dt-badge type="bulletin" text="Label"></dt-badge>
      </td>
      <td>
        <dt-badge kind="count" type="bulletin" text="6"></dt-badge>
      </td>
      <td>Used to provide temporary feedback to specific items in the interface, like live activity, notifications, and unread counts. </td>
    </tr>
    <tr>
      <th class="d-ta-left">Ai</th>
      <td>
        <dt-badge type="ai" text="Label" kind="label"></dt-badge>
      </td>
      <td><abbr class="d-fc-muted d-td-none d-fs-100" title="Not applicable">N/A</abbr></td>
      <td>To call out Dialpad Ai features.</td>
    </tr>
  </tbody>
</table>
<code-example only-show="code" source-code='&lt;dt-badge kind=&quot;label&quot; text=&quot;Label&quot; /&gt;
&lt;dt-badge type=&quot;info&quot; kind=&quot;label&quot; text=&quot;Label&quot; /&gt;
&lt;dt-badge type=&quot;positive&quot; kind=&quot;label&quot; text=&quot;Label&quot; /&gt;
&lt;dt-badge type=&quot;warning&quot; kind=&quot;label&quot; text=&quot;Label&quot; /&gt;
&lt;dt-badge type=&quot;critical&quot; kind=&quot;label&quot; text=&quot;Label&quot; /&gt;
&lt;dt-badge type=&quot;bulletin&quot; kind=&quot;label&quot; text=&quot;Label&quot; /&gt;
&lt;dt-badge type=&quot;ai&quot; text=&quot;Label&quot; kind=&quot;label&quot; /&gt;
&lt;dt-badge type=&quot;default&quot; text=&quot;1&quot; kind=&quot;count&quot; /&gt;
&lt;dt-badge type=&quot;info&quot; text=&quot;2&quot; kind=&quot;count&quot; /&gt;
&lt;dt-badge type=&quot;positive&quot; text=&quot;3&quot; kind=&quot;count&quot; /&gt;
&lt;dt-badge type=&quot;warning&quot; text=&quot;4&quot; kind=&quot;count&quot; /&gt;
&lt;dt-badge type=&quot;critical&quot; text=&quot;5&quot; kind=&quot;count&quot; /&gt;
&lt;dt-badge type=&quot;bulletin&quot; text=&quot;6&quot; kind=&quot;count&quot; /&gt;'>
<dt-badge kind="label" text="Label" />
<dt-badge type="info" kind="label" text="Label" />
<dt-badge type="positive" kind="label" text="Label" />
<dt-badge type="warning" kind="label" text="Label" />
<dt-badge type="critical" kind="label" text="Label" />
<dt-badge type="bulletin" kind="label" text="Label" />
<dt-badge type="ai" text="Label" kind="label" />
<dt-badge type="default" text="1" kind="count" />
<dt-badge type="info" text="2" kind="count" />
<dt-badge type="positive" text="3" kind="count" />
<dt-badge type="warning" text="4" kind="count" />
<dt-badge type="critical" text="5" kind="count" />
<dt-badge type="bulletin" text="6" kind="count" />
</code-example>
<h2 id="outlined" tabindex="-1" class="d-docsite--header-2">Outlined <a class="header-anchor d-link d-docsite--link d-link" href="#outlined" aria-hidden="true">#</a></h2>
<code-example source-code='&lt;dt-badge text=&quot;Label&quot; outlined /&gt;
&lt;dt-badge text=&quot;Label&quot; type=&quot;info&quot; outlined /&gt;
&lt;dt-badge text=&quot;Label&quot; type=&quot;positive&quot; outlined /&gt;
&lt;dt-badge text=&quot;Label&quot; type=&quot;warning&quot; outlined /&gt;
&lt;dt-badge text=&quot;Label&quot; type=&quot;critical&quot; outlined /&gt;
&lt;dt-badge text=&quot;1&quot; kind=&quot;count&quot; outlined /&gt;
&lt;dt-badge text=&quot;1&quot; type=&quot;info&quot; kind=&quot;count&quot; outlined /&gt;
&lt;dt-badge text=&quot;1&quot; type=&quot;positive&quot; kind=&quot;count&quot; outlined /&gt;
&lt;dt-badge text=&quot;1&quot; type=&quot;warning&quot; kind=&quot;count&quot; outlined /&gt;
&lt;dt-badge text=&quot;1&quot; type=&quot;critical&quot; kind=&quot;count&quot; outlined /&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-badge text="Label" outlined />
  <dt-badge text="Label" type="info" outlined />
  <dt-badge text="Label" type="positive" outlined />
  <dt-badge text="Label" type="warning" outlined />
  <dt-badge text="Label" type="critical" outlined />
  <dt-badge text="1" kind="count" outlined />
  <dt-badge text="1" type="info" kind="count" outlined />
  <dt-badge text="1" type="positive" kind="count" outlined />
  <dt-badge text="1" type="warning" kind="count" outlined />
  <dt-badge text="1" type="critical" kind="count" outlined />
</dt-stack>
</code-example>
<h2 id="subtle" tabindex="-1" class="d-docsite--header-2">Subtle <a class="header-anchor d-link d-docsite--link d-link" href="#subtle" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">At the moment, only the <code v-pre>bulletin</code> type has a subtle variant.</p>
<code-example source-code='&lt;dt-badge text=&quot;Label&quot; type=&quot;bulletin&quot; subtle /&gt;
&lt;dt-badge text=&quot;Label&quot; type=&quot;bulletin&quot; subtle outlined /&gt;
&lt;dt-badge text=&quot;1&quot; type=&quot;bulletin&quot; subtle kind=&quot;count&quot; /&gt;
&lt;dt-badge text=&quot;1&quot; type=&quot;bulletin&quot; subtle kind=&quot;count&quot; outlined /&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-badge text="Label" type="bulletin" subtle />
  <dt-badge text="Label" type="bulletin" subtle outlined />
  <dt-badge text="1" type="bulletin" subtle kind="count" />
  <dt-badge text="1" type="bulletin" subtle kind="count" outlined />
</dt-stack>
</code-example>
<h2 id="icon" tabindex="-1" class="d-docsite--header-2">Icon <a class="header-anchor d-link d-docsite--link d-link" href="#icon" aria-hidden="true">#</a></h2>
<code-example source-code='&lt;dt-badge type=&quot;default&quot; text=&quot;Label&quot; kind=&quot;label&quot;&gt;
  &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon-lightning-bolt :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-badge&gt;
&lt;dt-badge type=&quot;default&quot; text=&quot;Label&quot; kind=&quot;label&quot;&gt;
  &lt;template #endIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon-lightning-bolt :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-badge&gt;
&lt;dt-badge type=&quot;default&quot; text=&quot;Label&quot; kind=&quot;label&quot;&gt;
  &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon-lightning-bolt :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
  &lt;template #endIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon-lightning-bolt :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-badge&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-badge type="default" text="Label" kind="label">
    <template #startIcon="{ iconSize }">
      <dt-icon-lightning-bolt :size="iconSize" />
    </template>
  </dt-badge>
  <dt-badge type="default" text="Label" kind="label">
    <template #endIcon="{ iconSize }">
      <dt-icon-lightning-bolt :size="iconSize" />
    </template>
  </dt-badge>
  <dt-badge type="default" text="Label" kind="label">
    <template #startIcon="{ iconSize }">
      <dt-icon-lightning-bolt :size="iconSize" />
    </template>
    <template #endIcon="{ iconSize }">
      <dt-icon-lightning-bolt :size="iconSize" />
    </template>
  </dt-badge>
</dt-stack>
</code-example>
<h2 id="decorative" tabindex="-1" class="d-docsite--header-2">Decorative <a class="header-anchor d-link d-docsite--link d-link" href="#decorative" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Decorative badges label and classify items for quick recognition.</p>
<code-example source-code='&lt;dt-badge text=&quot;Label&quot; decoration=&quot;black-400&quot; /&gt;
&lt;dt-badge text=&quot;Label&quot; decoration=&quot;black-500&quot; /&gt;
&lt;dt-badge text=&quot;Label&quot; decoration=&quot;black-900&quot; /&gt;
&lt;dt-badge text=&quot;Label&quot; decoration=&quot;red-200&quot; /&gt;
...'>
<dt-stack direction="row" gap="200" align="baseline">
  <dt-stack gap="200">
    <dt-text kind="label" :size="300" density="200">Black</dt-text>
    <dt-badge text="Label" decoration="black-400" />
    <dt-badge text="Label" decoration="black-500" />
    <dt-badge text="Label" decoration="black-900" />
  </dt-stack>
  <dt-stack gap="200">
    <dt-text kind="label" :size="300" density="200">Red</dt-text>
    <dt-badge text="Label" decoration="red-200" />
    <dt-badge text="Label" decoration="red-300" />
    <dt-badge text="Label" decoration="red-400" />
  </dt-stack>
  <dt-stack gap="200">
    <dt-text kind="label" :size="300" density="200">Purple</dt-text>
    <dt-badge text="Label" decoration="purple-200" />
    <dt-badge text="Label" decoration="purple-300" />
    <dt-badge text="Label" decoration="purple-400" />
    <dt-badge text="Label" decoration="purple-500" />
  </dt-stack>
  <dt-stack gap="200">
    <dt-text kind="label" :size="300" density="200">Blue</dt-text>
    <dt-badge text="Label" decoration="blue-200" />
    <dt-badge text="Label" decoration="blue-300" />
    <dt-badge text="Label" decoration="blue-400" />
  </dt-stack>
  <dt-stack gap="200">
    <dt-text kind="label" :size="300" density="200">Green</dt-text>
    <dt-badge text="Label" decoration="green-300" />
    <dt-badge text="Label" decoration="green-400" />
    <dt-badge text="Label" decoration="green-500" />
  </dt-stack>
  <dt-stack gap="200">
    <dt-text kind="label" :size="300" density="200">Gold</dt-text>
    <dt-badge text="Label" decoration="gold-300" />
    <dt-badge text="Label" decoration="gold-400" />
    <dt-badge text="Label" decoration="gold-500" />
  </dt-stack>
  <dt-stack gap="200">
    <dt-text kind="label" :size="300" density="200">Magenta</dt-text>
    <dt-badge text="Label" decoration="magenta-200" />
    <dt-badge text="Label" decoration="magenta-300" />
    <dt-badge text="Label" decoration="magenta-400" />
  </dt-stack>
</dt-stack>
</code-example>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use for categories of items with a limited number of options (eg. call categories, AI moments).</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use for categories of items with an unlimited or unknown number of options (eg. user-defined contact labels, RTA cards, contact centers).</li>
<li class="d-docsite--list-element">Use for single items that are not part of a larger group.</li>
<li class="d-docsite--list-element">Use for decoration only, to bring attention to part of the UI by using colors.</li>
<li class="d-docsite--list-element">Use with <code v-pre>kind=count</code>, nor with any <code v-pre>type</code> that is not <code v-pre>default</code>.</li>
<li class="d-docsite--list-element">Use in combination with an icon.</li>
<li class="d-docsite--list-element">Change the customize the Badge's background color text style,</li>
<li class="d-docsite--list-element">Extend the decorative slot color beyond what Dialtone provides.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="best-practices-1" tabindex="-1" class="d-docsite--header-3">Best Practices <a class="header-anchor d-link d-docsite--link d-link" href="#best-practices-1" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Favor lighter shades over darker ones.</li>
<li class="d-docsite--list-element">Use each color hue before using the next available shade.</li>
</ul>
<h2 id="examples" tabindex="-1" class="d-docsite--header-2">Examples <a class="header-anchor d-link d-docsite--link d-link" href="#examples" aria-hidden="true">#</a></h2>
<h3 id="label-1" tabindex="-1" class="d-docsite--header-3">Label <a class="header-anchor d-link d-docsite--link d-link" href="#label-1" aria-hidden="true">#</a></h3>
<code-example only-show="demo">
<dt-stack gap="200">
  <dt-stack direction="row" gap="100">
    <dt-badge text="Co-host" />
    <dt-badge text="Customer" />
    <dt-badge text="Locked">
      <template #startIcon="{ iconSize }">
        <dt-icon-lock :size="iconSize" />
      </template>
    </dt-badge>
    <dt-badge text="Chat log">
      <template #startIcon="{ iconSize }">
        <dt-icon-message :size="iconSize" />
      </template>
    </dt-badge>
  </dt-stack>
  <dt-stack direction="row" gap="00">
    <dt-badge text="In progress" type="info" />
    <dt-badge text="Beta" type="info" />
    <dt-badge text="Draft" type="info" />
  </dt-stack>
  <dt-stack direction="row" gap="100">
    <dt-badge text="Overdue" type="warning" />
  </dt-stack>
  <dt-stack direction="row" gap="100">
    <dt-badge text="Resolved" type="positive" />
  </dt-stack>
  <dt-stack direction="row" gap="100">
    <dt-badge text="Recording" type="critical">
      <template #startIcon="{ iconSize }">
        <dt-icon-record-filled :size="iconSize" />
      </template>
    </dt-badge>
  </dt-stack>
  <dt-stack direction="row" gap="100">
    <dt-badge text="Live" type="bulletin" />
    <dt-badge text="Presenter" type="bulletin" />
  </dt-stack>
  <dt-stack direction="row" gap="100">
    <dt-badge type="ai" text="Ai Notes" />
    <dt-badge type="ai" text="Ai Suggestion" />
    <dt-badge type="ai" text="Ai enabled" />
    <dt-badge type="ai" text="Ai Transcript" />
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="count-1" tabindex="-1" class="d-docsite--header-3">Count <a class="header-anchor d-link d-docsite--link d-link" href="#count-1" aria-hidden="true">#</a></h3>
<code-example only-show="demo">
<dt-stack gap="200">
  <dt-stack direction="row" gap="100">
    <dt-badge kind="count" type="positive" text="5%">
      <template #startIcon="{ iconSize }">
        <dt-icon-arrow-up :size="iconSize" />
      </template>
    </dt-badge>
  </dt-stack>
  <dt-stack direction="row" gap="100">
    <dt-badge kind="count" type="critical" text="-12%">
      <template #startIcon="{ iconSize }">
        <dt-icon-arrow-down :size="iconSize" />
      </template>
    </dt-badge>
  </dt-stack>
  <dt-stack direction="row" gap="100">
    <dt-badge kind="count" type="bulletin" text="1" />
    <dt-badge kind="count" type="bulletin" text="18" />
    <dt-badge kind="count" type="bulletin" text="99+" />
  </dt-stack>
</dt-stack>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="badge"></component-vue-api><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="badge"></component-class-table></div></template>


<script setup>
  import {
    DtIconLightningBolt,
    DtIconLock,
    DtIconMessage,
    DtIconRecordFilled,
    DtIconArrowUp,
    DtIconArrowDown,
  } from '@dialpad/dialtone-icons/vue';
</script>